import {
  asStableCallback,
  useStableCallback,
} from "@bezoned/shared/hooks/use-stable-callback.js";
import { routes } from "@bezoned/shared/routes.js";
import { pages } from "@microsoft/teams-js";
import { useForm } from "@tanstack/react-form";
import { useEffect, useState } from "react";

import { recordErrorInSentry } from "../../../utils/init-sentry.js";
import { SelectChannel } from "./configuration/select-channel.jsx";
import { SelectTeam } from "./configuration/select-team.jsx";

// Component to sync form validity with Teams config
const ValidityStateSync = ({
  msTeamId,
  channelUuid,
}: {
  msTeamId: string;
  channelUuid: string;
}) => {
  useEffect(() => {
    const isValid = Boolean(msTeamId && channelUuid);
    pages.config.setValidityState(isValid);
  }, [msTeamId, channelUuid]);

  return null;
};

export const MeetingSidePanelConfiguration = () => {
  const [error, setError] = useState<string | null>(null);

  const form = useForm({
    defaultValues: {
      channelUuid: "",
      msTeamId: "",
    },
    onSubmit: async ({ value }) => {
      try {
        setError(null);
        // Build the URLs for the meeting side panel
        // Note: routes already contain the leading slash
        const contentUrl = new URL(
          routes.protected.meetingSidePanel,
          window.location.origin
        );
        contentUrl.searchParams.set("msTeamId", value.msTeamId);
        contentUrl.searchParams.set("channelUuid", value.channelUuid);

        // Debug: Log the generated URL
        console.log(
          "MeetingSidePanelConfiguration - Generated URL:",
          contentUrl.toString()
        );
        console.log("MeetingSidePanelConfiguration - Config values:", {
          channelUuid: value.channelUuid,
          msTeamId: value.msTeamId,
        });

        // Set the configuration for Teams
        await pages.config.setConfig({
          contentUrl: contentUrl.toString(),
          entityId: "bezoned-meeting",
          suggestedDisplayName: "BeZoned Meeting",
          websiteUrl: contentUrl.toString(),
        });

        console.log("MeetingSidePanelConfiguration - Config set successfully");
      } catch (error) {
        setError(
          "There was an issue saving your selection. This has been reported."
        );
        recordErrorInSentry(error);
      }
    },
  });

  // Register save handler on mount
  useEffect(() => {
    pages.config.registerOnSaveHandler(async (saveEvent) => {
      try {
        await form.handleSubmit();
        saveEvent.notifySuccess();
      } catch {
        saveEvent.notifyFailure("Failed to save meeting configuration");
      }
    });
  }, [form]);
  const handleSelector89 = useStableCallback((state) => ({
    channelUuid: state.values.channelUuid,
    msTeamId: state.values.msTeamId,
  }));

  return (
    <div className="flex flex-col gap-6">
      {/* Update Teams validity state when form values change */}
      <form.Subscribe selector={handleSelector89}>
        {({ msTeamId, channelUuid }) => (
          <ValidityStateSync channelUuid={channelUuid} msTeamId={msTeamId} />
        )}
      </form.Subscribe>
      {/* Instructions */}
      <div>
        <p className="text-bzd-gray-600 text-sm leading-relaxed">
          To open documents, notes, planners, whiteboards etc., please select
          where in BeZoned they are located
        </p>
      </div>

      {/* Global Error Message */}
      {Boolean(error) && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Office Selection Field */}
      <form.Field
        name="msTeamId"
        validators={{
          onChange: ({ value }) =>
            value ? undefined : "Please select an office",
        }}
      >
        {(field) => <SelectTeam field={field} label="Office" />}
      </form.Field>

      {/* Room/Table Selection Field */}
      <form.Field
        name="channelUuid"
        validators={{
          onChange: ({ value, fieldApi }) => {
            // Only validate if user has interacted with the field or team is selected
            const msTeamId = fieldApi.form.getFieldValue("msTeamId");
            if (!msTeamId || value) {
              return; // Don't show error if no team selected yet or value is valid
            }

            return "Please select a room or table";
          },
          onChangeListenTo: ["msTeamId"],
        }}
      >
        {(field) => (
          <form.Subscribe
            selector={asStableCallback((state) => state.values.msTeamId)}
          >
            {(msTeamId) => (
              <SelectChannel
                field={field}
                label="Table/room"
                msTeamId={msTeamId}
              />
            )}
          </form.Subscribe>
        )}
      </form.Field>
    </div>
  );
};
